import { type MessageDescriptor } from 'react-intl';

import { subdivisionLabelMessages } from './country-subdivision-labels.messages';

/**
 * Maps country names to their locale-appropriate subdivision field label descriptor.
 * Countries with no meaningful subdivision (e.g. city-states) are omitted.
 *
 * Source: heidi_country_subdivision_labels - Country Subdivision Labels.csv
 */
export const COUNTRY_SUBDIVISION_LABELS: Readonly<Record<string, MessageDescriptor>> = {
  Afghanistan: subdivisionLabelMessages.province,
  Albania: subdivisionLabelMessages.county,
  Algeria: subdivisionLabelMessages.province,
  Andorra: subdivisionLabelMessages.parish,
  Angola: subdivisionLabelMessages.province,
  'Antigua and Barbuda': subdivisionLabelMessages.parish,
  Argentina: subdivisionLabelMessages.province,
  Armenia: subdivisionLabelMessages.province,
  Australia: subdivisionLabelMessages.stateTerritory,
  Austria: subdivisionLabelMessages.state,
  Azerbaijan: subdivisionLabelMessages.district,
  Bahamas: subdivisionLabelMessages.district,
  Bahrain: subdivisionLabelMessages.governorate,
  Bangladesh: subdivisionLabelMessages.division,
  Barbados: subdivisionLabelMessages.parish,
  Belarus: subdivisionLabelMessages.oblast,
  Belgium: subdivisionLabelMessages.provinceRegion,
  Belize: subdivisionLabelMessages.district,
  Benin: subdivisionLabelMessages.department,
  Bhutan: subdivisionLabelMessages.district,
  Bolivia: subdivisionLabelMessages.department,
  'Bosnia and Herzegovina': subdivisionLabelMessages.cantonEntity,
  Botswana: subdivisionLabelMessages.district,
  Brazil: subdivisionLabelMessages.state,
  Brunei: subdivisionLabelMessages.district,
  Bulgaria: subdivisionLabelMessages.province,
  'Burkina Faso': subdivisionLabelMessages.region,
  Burundi: subdivisionLabelMessages.province,
  'Cabo Verde': subdivisionLabelMessages.municipality,
  Cambodia: subdivisionLabelMessages.province,
  Cameroon: subdivisionLabelMessages.region,
  Canada: subdivisionLabelMessages.provinceTerritories,
  'Central African Republic': subdivisionLabelMessages.prefecture,
  Chad: subdivisionLabelMessages.province,
  Chile: subdivisionLabelMessages.region,
  China: subdivisionLabelMessages.provinceMunicipality,
  Colombia: subdivisionLabelMessages.department,
  Comoros: subdivisionLabelMessages.island,
  'Congo (DRC)': subdivisionLabelMessages.province,
  'Congo (Republic)': subdivisionLabelMessages.department,
  'Costa Rica': subdivisionLabelMessages.province,
  "Côte d'Ivoire": subdivisionLabelMessages.districtRegion,
  Croatia: subdivisionLabelMessages.county,
  Cuba: subdivisionLabelMessages.province,
  Cyprus: subdivisionLabelMessages.district,
  'Czech Republic': subdivisionLabelMessages.region,
  Denmark: subdivisionLabelMessages.region,
  Djibouti: subdivisionLabelMessages.region,
  Dominica: subdivisionLabelMessages.parish,
  'Dominican Republic': subdivisionLabelMessages.province,
  Ecuador: subdivisionLabelMessages.province,
  Egypt: subdivisionLabelMessages.governorate,
  'El Salvador': subdivisionLabelMessages.department,
  'Equatorial Guinea': subdivisionLabelMessages.province,
  Eritrea: subdivisionLabelMessages.region,
  Estonia: subdivisionLabelMessages.county,
  Eswatini: subdivisionLabelMessages.region,
  Ethiopia: subdivisionLabelMessages.region,
  Fiji: subdivisionLabelMessages.division,
  Finland: subdivisionLabelMessages.region,
  France: subdivisionLabelMessages.regionDepartment,
  Gabon: subdivisionLabelMessages.province,
  Gambia: subdivisionLabelMessages.division,
  Georgia: subdivisionLabelMessages.region,
  Germany: subdivisionLabelMessages.state,
  Ghana: subdivisionLabelMessages.region,
  Greece: subdivisionLabelMessages.region,
  Grenada: subdivisionLabelMessages.parish,
  Guatemala: subdivisionLabelMessages.department,
  Guinea: subdivisionLabelMessages.region,
  'Guinea-Bissau': subdivisionLabelMessages.region,
  Guyana: subdivisionLabelMessages.region,
  Haiti: subdivisionLabelMessages.department,
  Honduras: subdivisionLabelMessages.department,
  Hungary: subdivisionLabelMessages.county,
  Iceland: subdivisionLabelMessages.region,
  India: subdivisionLabelMessages.stateUnionTerritory,
  Indonesia: subdivisionLabelMessages.province,
  Iran: subdivisionLabelMessages.province,
  Iraq: subdivisionLabelMessages.governorate,
  Ireland: subdivisionLabelMessages.county,
  Israel: subdivisionLabelMessages.district,
  Italy: subdivisionLabelMessages.region,
  Jamaica: subdivisionLabelMessages.parish,
  Japan: subdivisionLabelMessages.prefecture,
  Jordan: subdivisionLabelMessages.governorate,
  Kazakhstan: subdivisionLabelMessages.region,
  Kenya: subdivisionLabelMessages.county,
  Kiribati: subdivisionLabelMessages.islandGroup,
  Kosovo: subdivisionLabelMessages.municipality,
  Kuwait: subdivisionLabelMessages.governorate,
  Kyrgyzstan: subdivisionLabelMessages.region,
  Laos: subdivisionLabelMessages.province,
  Latvia: subdivisionLabelMessages.municipality,
  Lebanon: subdivisionLabelMessages.governorate,
  Lesotho: subdivisionLabelMessages.district,
  Liberia: subdivisionLabelMessages.county,
  Libya: subdivisionLabelMessages.district,
  Liechtenstein: subdivisionLabelMessages.municipality,
  Lithuania: subdivisionLabelMessages.county,
  Luxembourg: subdivisionLabelMessages.canton,
  Madagascar: subdivisionLabelMessages.region,
  Malawi: subdivisionLabelMessages.regionDistrict,
  Malaysia: subdivisionLabelMessages.stateFederalTerritory,
  Maldives: subdivisionLabelMessages.atoll,
  Mali: subdivisionLabelMessages.region,
  Malta: subdivisionLabelMessages.region,
  'Marshall Islands': subdivisionLabelMessages.atollIsland,
  Mauritania: subdivisionLabelMessages.region,
  Mauritius: subdivisionLabelMessages.district,
  Mexico: subdivisionLabelMessages.state,
  Micronesia: subdivisionLabelMessages.state,
  Moldova: subdivisionLabelMessages.district,
  Monaco: subdivisionLabelMessages.quarter,
  Mongolia: subdivisionLabelMessages.province,
  Montenegro: subdivisionLabelMessages.municipality,
  Morocco: subdivisionLabelMessages.region,
  Mozambique: subdivisionLabelMessages.province,
  Myanmar: subdivisionLabelMessages.regionState,
  Namibia: subdivisionLabelMessages.region,
  Nauru: subdivisionLabelMessages.district,
  Nepal: subdivisionLabelMessages.province,
  Netherlands: subdivisionLabelMessages.province,
  'New Zealand': subdivisionLabelMessages.region,
  Nicaragua: subdivisionLabelMessages.department,
  Niger: subdivisionLabelMessages.region,
  Nigeria: subdivisionLabelMessages.state,
  'North Macedonia': subdivisionLabelMessages.municipality,
  Norway: subdivisionLabelMessages.county,
  Oman: subdivisionLabelMessages.governorate,
  Pakistan: subdivisionLabelMessages.provinceTerritories,
  Palau: subdivisionLabelMessages.state,
  Panama: subdivisionLabelMessages.province,
  'Papua New Guinea': subdivisionLabelMessages.province,
  Paraguay: subdivisionLabelMessages.department,
  Peru: subdivisionLabelMessages.region,
  Philippines: subdivisionLabelMessages.province,
  Poland: subdivisionLabelMessages.voivodeship,
  Portugal: subdivisionLabelMessages.districtRegion,
  Qatar: subdivisionLabelMessages.municipality,
  Romania: subdivisionLabelMessages.county,
  Russia: subdivisionLabelMessages.federalSubject,
  Rwanda: subdivisionLabelMessages.province,
  'Saint Kitts and Nevis': subdivisionLabelMessages.parish,
  'Saint Lucia': subdivisionLabelMessages.quarter,
  'Saint Vincent and the Grenadines': subdivisionLabelMessages.parish,
  Samoa: subdivisionLabelMessages.district,
  'San Marino': subdivisionLabelMessages.municipality,
  'São Tomé and Príncipe': subdivisionLabelMessages.district,
  'Saudi Arabia': subdivisionLabelMessages.region,
  Senegal: subdivisionLabelMessages.region,
  Serbia: subdivisionLabelMessages.district,
  Seychelles: subdivisionLabelMessages.district,
  'Sierra Leone': subdivisionLabelMessages.provinceDistrict,
  Slovakia: subdivisionLabelMessages.region,
  Slovenia: subdivisionLabelMessages.statisticalRegion,
  'Solomon Islands': subdivisionLabelMessages.province,
  Somalia: subdivisionLabelMessages.federalMemberState,
  'South Africa': subdivisionLabelMessages.province,
  'South Sudan': subdivisionLabelMessages.state,
  Spain: subdivisionLabelMessages.autonomousCommunity,
  'Sri Lanka': subdivisionLabelMessages.province,
  Sudan: subdivisionLabelMessages.state,
  Suriname: subdivisionLabelMessages.district,
  Sweden: subdivisionLabelMessages.county,
  Switzerland: subdivisionLabelMessages.canton,
  Syria: subdivisionLabelMessages.governorate,
  Taiwan: subdivisionLabelMessages.countySpecialMunicipality,
  Tajikistan: subdivisionLabelMessages.region,
  Tanzania: subdivisionLabelMessages.region,
  Thailand: subdivisionLabelMessages.province,
  'Timor-Leste': subdivisionLabelMessages.municipality,
  Togo: subdivisionLabelMessages.region,
  Tonga: subdivisionLabelMessages.division,
  Tunisia: subdivisionLabelMessages.governorate,
  Turkey: subdivisionLabelMessages.province,
  Turkmenistan: subdivisionLabelMessages.region,
  Tuvalu: subdivisionLabelMessages.island,
  Uganda: subdivisionLabelMessages.district,
  Ukraine: subdivisionLabelMessages.oblast,
  'United Arab Emirates': subdivisionLabelMessages.emirate,
  'United Kingdom': subdivisionLabelMessages.countyNation,
  'United States': subdivisionLabelMessages.state,
  Uruguay: subdivisionLabelMessages.department,
  Uzbekistan: subdivisionLabelMessages.region,
  Vanuatu: subdivisionLabelMessages.province,
  Venezuela: subdivisionLabelMessages.state,
  Vietnam: subdivisionLabelMessages.provinceMunicipality,
  Yemen: subdivisionLabelMessages.governorate,
  Zambia: subdivisionLabelMessages.province,
  Zimbabwe: subdivisionLabelMessages.province,
};

/**
 * Returns the MessageDescriptor for the locale-appropriate subdivision label
 * for the given country, or null if the country has no entry (e.g. city-states).
 * Use intl.formatMessage(descriptor) to get the translated string.
 */
export function getSubdivisionLabelDescriptor(
  countryName: string | null | undefined
): MessageDescriptor | null {
  if (!countryName) return null;
  return COUNTRY_SUBDIVISION_LABELS[countryName] ?? null;
}
